import { getRouteApi } from '@tanstack/react-router';
import { signInWithCustomToken, signInWithPopup, signOut } from 'firebase/auth';
import { auth, googleProvider, useAuth } from '@discovr/common';
import { domainJoin } from '@discovr/api';
import { InviteAcceptPage } from '@discovr/ui';

const joinAcceptRoute = getRouteApi('/join/accept');

export default function JoinAcceptPage() {
  const { token } = joinAcceptRoute.useSearch();
  const { user, loading: authLoading } = useAuth();

  const signInWithJoinToken = async (customToken: string) => {
    // Clear out any pre-existing session in this browser first — otherwise
    // the old account can remain the shared IndexedDB "current user" and
    // reappear on refresh instead of the account that just signed up.
    await signOut(auth).catch(() => {});
    await signInWithCustomToken(auth, customToken);
  };

  return (
    <InviteAcceptPage
      token={token}
      variant="invite"
      verifyFn={(token) => domainJoin.verifyJoinAccept(token)}
      acceptFn={(body) =>
        domainJoin.acceptJoin({
          token: body.token,
          password: body.password,
          first_name: body.first_name ?? '',
          last_name: body.last_name ?? '',
        })
      }
      orgNameKey="brand_name"
      currentUserEmail={authLoading ? undefined : (user?.email ?? null)}
      onSignOutCurrentUser={() => signOut(auth)}
      signInWithToken={signInWithJoinToken}
      onGoogleAccept={async () => {
        const result = await signInWithPopup(auth, googleProvider);
        const idToken = await result.user.getIdToken();
        // Sign out of the popup session immediately — AuthProvider's
        // onAuthStateChanged gate would otherwise race /auth/login against a
        // users row that doesn't exist yet (it's only created by the BE call
        // below, after it verifies the token's email matches the request).
        await signOut(auth).catch(() => {});
        const accepted = await domainJoin.acceptJoinGoogle({ token: token ?? '', id_token: idToken });
        await signInWithJoinToken(accepted.firebase_custom_token);
      }}
    />
  );
}
